import { useAuth } from "@clerk/clerk-react";
import { Box, Grid, Link, useMediaQuery, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import {
  CheckoutState,
  SplashPage,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AddOnItem,
  AddOnItemName,
  OrganizationProProfileStatus,
  SearchParams,
  StripeSubscription,
  SubscriptionType,
  UpgradeDialogSource,
  VaOrganizationProfileStatus,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { InstagramOnboardingFlowOption } from "features/Auth/InstagramOnboarding/schema";
import { eligibleForProDiscount } from "features/Home/Banner/helpers";
import { YearlyPlanOnly } from "features/Home/UpgradeDialog/Plan/helpers";
import { fetcherAuth } from "utils/api";
import {
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  INSTAGRAM_ONBOARDING_CHOICE,
  INSTAGRAM_ONBOARDING_FLOW,
} from "utils/localStorage";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { useLogin } from "utils/useLogin";

import ConfirmPaymentForm from "./components/ConfirmPaymentForm";
import InvoicePreview from "./components/InvoicePreview";
import NotInterestedButton from "./components/NotInterestedButton";
import PaymentForm from "./components/PaymentForm";
import PlanDetails from "./components/PlanDetails";
import SkeletonInvoiceForm from "./components/SkeletonInvoiceForm";
import SkeletonPaymentForm from "./components/SkeletonPaymentForm";
import {
  LeftSidebarWrapper,
  MainWrapper,
  RightSidebarWrapper,
} from "./components/Wrapper";
import { getDetails } from "./helpers";
import { SplashPageSourceBackRoute, StripeCustomer } from "./schema";
import { useCheckout } from "./useCheckout";

interface Props {
  openCheckoutDialog?: CheckoutState;
  onSuccess?: () => void;
  onBack?: () => void;
  noBackButton?: boolean;
  upgradeDialogSourceOverride?: UpgradeDialogSource | null;
}

const StripeCheckout = ({
  openCheckoutDialog,
  onSuccess,
  onBack,
  noBackButton = false,
  upgradeDialogSourceOverride,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setErrorAlert } = useContext(AlertContext);
  const {
    currentOrg,
    setCurrentOrg,
    organizationVaProfile,
    organizationProProfile,
  } = useContext(OrganizationUserContext);
  const {
    subscription,
    setSubscription,
    setOpenCheckoutDialog,
    upgradeDialogSource: upgradeDialogSourceFromContext,
    setUpgradeDialogSource,
    plans,
    addOns,
    setPlan: setSubscriptionPlan,
    setTab,
  } = useContext(SubscriptionContext);
  const upgradeDialogSource =
    upgradeDialogSourceOverride ?? upgradeDialogSourceFromContext;
  const {
    plan,
    customer,
    setCustomer,
    promoCode,
    noNeedsToPay,
    options,
    setOptions,
    addOnItems,
    setRequiresBillingAddress,
    invoice,
    setPageSource,
    setSource,
    setCoupon,
    setPromoCode,
    setPlan,
    setAddOnItems,
  } = useContext(CheckoutContext);

  const { state } = useLocation();

  const getState = () => {
    if (openCheckoutDialog?.plan) {
      return openCheckoutDialog;
    } else {
      return { ...state } as CheckoutState;
    }
  };

  const {
    plan: planFromState,
    pageSource,
    source,
    domainInfo,
    splashPageSource,
    fromSubscribePage,
  } = getState();
  const { isNewUser } = useLogin();

  const isInstagramOnboardingFlow = splashPageSource === SplashPage.instagram;
  const isOOOFlow = splashPageSource === SplashPage.ooo;
  const isProBundleFlow = splashPageSource === SplashPage.pro_bundle;

  const isDialogOpen = Boolean(openCheckoutDialog?.plan);

  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { updatePreviewInvoice } = useCheckout();

  const isVaPlan = plan === Plans.VA || plan === Plans.VA_YEARLY;
  const isAutopilotPlan =
    plan === Plans.AUTOPILOT || plan === Plans.AUTOPILOT_YEARLY;
  const isProPlan = plan === Plans.PRO || plan === Plans.PRO_YEARLY;

  const isAddOnItem =
    (addOns && plan && plan in addOns) ||
    splashPageSource === SplashPage.va_bundled;

  const isSwitchingSubscription =
    subscription?.subscription?.status === SubscriptionType.ACTIVE &&
    !isAddOnItem;
  const details = getDetails(plan, plans, addOns, splashPageSource);
  const basedUrl = `${window.location.protocol}//${window.location.host}/`;

  const getReturnUrl = () => {
    if (splashPageSource === SplashPage.va_bundled) {
      return `${basedUrl}${routes.vaBundledIntakeForm}?${SearchParams.INVOICE_ID}=${invoice?.id}`;
    } else if (isVaPlan) {
      return `${basedUrl}${routes.onboardingVaInfluencer}?checkout=success`;
    } else if (isProBundleFlow || splashPageSource === SplashPage.pro) {
      return `${basedUrl}${
        routes.aboutOnboarding
      }?checkout=success&source=${getSource()}`;
    } else if (
      plan === AddOnItemName.email_volume_monthly ||
      plan === AddOnItemName.email_volume_yearly ||
      plan === AddOnItemName.email_volume_pro_monthly ||
      plan === AddOnItemName.email_volume_pro_yearly
    ) {
      return `${basedUrl}${routes.settings}${location.search}?checkout=success`;
    } else if (splashPageSource === SplashPage.annual) {
      if (isNewUser) {
        return `${basedUrl}${routes.onboardingInfluencer}?checkout=success`;
      } else {
        return `${basedUrl}${routes.settings}?checkout=success`;
      }
    } else {
      return `${basedUrl}${pageSource || routes.settings}?checkout=success`;
    }
  };

  const totalDue = Number((invoice?.amount_due || 0) / 100);

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      identifyUser(res.organizationUser.id);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const getOrCreateCustomer = async (organizationId: number) => {
    setLoading(true);
    try {
      const { customer } = await fetcherAuth(
        getToken,
        `/api/organization/${organizationId}/payments/customer`,
        "POST",
      );
      if (customer?.address?.country && customer?.address?.postal_code) {
        setRequiresBillingAddress(false);
      }
      setCustomer(customer);
      const {
        pageSource,
        source,
        coupon: emailCoupon,
        domainInfo,
        splashPageSource,
      } = getState();
      let updatedPromoCode = promoCode;
      if (
        !updatedPromoCode &&
        !promoCode.id &&
        customer.affiliate_promo_code &&
        !emailCoupon
      ) {
        setPromoCode(customer.affiliate_promo_code);
        updatedPromoCode = customer.affiliate_promo_code;
      }
      const presetAddOns = getPresetAddOnItems(plan);
      if (emailCoupon) setCoupon(emailCoupon);
      if (source) setSource(source);
      if (pageSource) setPageSource(pageSource);
      updatePreviewInvoice({
        plan,
        organizationId,
        customer,
        coupon: updatedPromoCode,
        addOnItems: presetAddOns,
        splashPageSource,
        removePromoCode: Boolean(emailCoupon),
        domainInfo,
      });
    } catch (error) {
      setErrorAlert(error);
      setRequiresBillingAddress(true);
    } finally {
      setLoading(false);
    }
  };

  const generateIntentSecret = async () => {
    if (!currentOrg) return;
    try {
      const { clientSecret } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription/intent`,
        "POST",
      );
      setOptions({
        clientSecret,
      });
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const createSubscription = async () => {
    // Only use for Pro and VA plans.
    setConfirmLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription/create`,
        "POST",
        {},
        {
          plan,
          stripeCustomerId: customer?.id,
          source,
          pageSource,
          splashPageSource,
          promoCode: promoCode?.id,
          couponName: promoCode?.name,
          bundledAddOns: addOnItems,
          domainInfo,
        },
      );
      await postSuccessCheckout(res.subscription, res.items);
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const switchSubscription = async () => {
    // Only use for Pro and VA plans.
    try {
      setConfirmLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription/switch`,
        "POST",
        {},
        {
          plan,
          stripeCustomerId: customer?.id,
          source,
          pageSource,
          isInstagramOnboardingFlow,
          amountDue: invoice?.amount_due,
          promoCode: promoCode?.id,
          couponName: promoCode?.name,
          bundledAddOns: addOnItems,
          splashPageSource,
        },
      );
      await postSuccessCheckout(res.subscription, res.items);
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev: StripeCustomer) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const createAddOns = async () => {
    if (confirmLoading) return;
    const data: Map = {
      stripeCustomerId: customer?.id,
      source,
      promoCode: promoCode?.id,
      couponName: promoCode?.name,
      domainInfo,
    };
    if (invoice?.id) {
      data.invoiceId = invoice.id;
    }
    if (addOnItems && addOnItems.length > 0) {
      data.items = addOnItems;
    } else {
      data.items = [
        {
          name: plan,
          quantity: 1,
        },
      ];
    }
    if (!customer?.id) {
      return;
    }
    try {
      setConfirmLoading(true);

      const { addOnItems } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/create-multi`,
        "POST",
        {},
        data,
      );
      const purchasedPitchingTemplates = addOnItems.filter(
        (item: AddOnItem) => item.itemName === AddOnItemName.pitching_templates,
      );
      if (purchasedPitchingTemplates.length > 0) {
        window.open(
          `${basedUrl}${routes.intakeForms}/${purchasedPitchingTemplates[0].id}`,
          "_self",
        );
        return;
      }
      const returnUrl = getReturnUrl();
      window.open(returnUrl, "_self");
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (isAddOnItem) {
      await createAddOns();
    } else if (isSwitchingSubscription) {
      await switchSubscription();
    } else {
      await createSubscription();
    }
  };

  const trackSubscription = async (abandonedCheckout?: boolean) => {
    if (!plan) return;
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/payments/subscription/track`,
      "POST",
      {},
      {
        plan,
        isAbandoned: abandonedCheckout,
        isInstagramOnboardingFlow,
        fromSubscribePage,
      },
    );
  };

  const postSuccessCheckout = async (
    subscription: StripeSubscription,
    items: AddOnItem[],
  ) => {
    // This function is called if a Subscription or a recurring AddOnItem is purchased.
    if (subscription?.status !== SubscriptionType.ACTIVE) {
      // Failed payment, redirect to Settings page so they can retry
      window.open(
        `${basedUrl}dashboard/settings?checkout=failed_payment`,
        "_self",
      );
      return;
    }
    if (
      // @ts-ignore
      typeof window.fbq !== "undefined" &&
      // @ts-ignore
      typeof window.fbq === "function"
    ) {
      // @ts-ignore
      window.fbq("track", "Subscribe", {
        value: details?.price?.toFixed(2),
        currency: "USD",
        predicted_ltv: details?.price
          ? (details?.price * 5).toFixed(2)
          : "0.00",
      });
    }

    if (
      // @ts-ignore
      typeof window.ttq !== "undefined" &&
      // @ts-ignore
      typeof window.ttq.track === "function"
    ) {
      // @ts-ignore
      window.ttq.track("Subscribe", {
        contents: [
          {
            content_id: details.product,
            content_type: "product",
            content_name: details.display_name,
          },
        ],
        value: details?.price?.toFixed(2),
        currency: "USD",
      });
    }

    if (
      // @ts-ignore
      typeof window.oaiq !== "undefined" &&
      // @ts-ignore
      typeof window.oaiq === "function"
    ) {
      // @ts-ignore
      window.oaiq("measure", "subscription_created", {
        type: "plan_enrollment",
        plan_id: details.product,
        amount: details?.price ? Math.round(details.price * 100) : undefined,
        currency: "USD",
      });
    }

    if (
      // @ts-ignore
      typeof window.gtag !== "undefined" &&
      // @ts-ignore
      typeof window.gtag === "function"
    ) {
      // @ts-ignore
      window.gtag("event", "conversion", {
        send_to: "AW-18111742383/AR1kCICDlaIcEK-DrbxD",
        value: details?.price ?? 1.0,
        currency: "USD",
      });
    }

    setSubscription((prev) => {
      if (prev) {
        prev.subscription = subscription;
      }
      return prev;
    });
    setCurrentOrg((prev) => {
      if (prev) {
        return {
          ...prev,
          isVaUser: isVaPlan,
          isProUser: isProPlan,
          isAutopilotUser: isAutopilotPlan,
        };
      }
      return prev;
    });

    const needsOnboarding =
      (isVaPlan &&
        organizationVaProfile?.status !==
          VaOrganizationProfileStatus.completed) ||
      (isProPlan &&
        organizationProProfile?.status !==
          OrganizationProProfileStatus.completed);

    if (subscription?.planName) {
      let url = `${basedUrl}${routes.checkoutSuccess}?plan=${subscription?.planName}&isOnboarding=${needsOnboarding}`;
      if (fromSubscribePage && !isVaPlan && !isAutopilotPlan) {
        url += "&fromSubscribePage=true";
      }
      window.open(url, "_self");
      return;
    }

    const addOnTemplate =
      items && items?.length > 0
        ? items?.find((x) => x.itemName === AddOnItemName.pitching_templates)
        : null;
    if (!isVaPlan && addOnTemplate?.id) {
      navigate(`/${routes.intakeForms}/${addOnTemplate.id}`);
    } else if (isVaPlan || !isDialogOpen) {
      const returnUrl = getReturnUrl();
      window.open(returnUrl, "_self");
    } else {
      await fetchUserInfo();
      setOpenCheckoutDialog(null);
      setUpgradeDialogSource(null);
      searchParams.set("checkout", "success");
      setSearchParams(searchParams);
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const getPresetAddOnItems = (plan: Plans | AddOnItemName | any) => {
    const customerSupportPreset = addOns?.[AddOnItemName.customer_support]
      ? [{ name: AddOnItemName.customer_support, quantity: 1 }]
      : [];

    // Return the right add on items based on the SplashPage source.
    if (isOOOFlow && plan !== AddOnItemName.oui_studio) {
      return [
        {
          name: AddOnItemName.oui_studio,
          quantity: 1,
        },
        ...customerSupportPreset,
      ];
    }
    if (isProBundleFlow && plan !== AddOnItemName.partnership_data) {
      return [
        {
          name: AddOnItemName.partnership_data,
          quantity: 1,
        },
        ...customerSupportPreset,
      ];
    }
    if (isAddOnItem && splashPageSource !== SplashPage.va_bundled) {
      return [
        {
          name: plan as AddOnItemName,
          quantity: 1,
        },
      ];
    }
    return customerSupportPreset;
  };

  const keepTabForProDiscount =
    eligibleForProDiscount(currentOrg, plans, subscription) &&
    subscription?.subscription?.planName === Plans.PRO;
  const initializePlan = () => {
    if (
      isMobileScreen &&
      !YearlyPlanOnly?.includes(upgradeDialogSource as UpgradeDialogSource) &&
      splashPageSource !== SplashPage.va_money_back &&
      !keepTabForProDiscount
    ) {
      if (planFromState === Plans.VA_YEARLY || planFromState === Plans.VA) {
        setPlan(Plans.VA);
        setTab(SubscriptionTab.monthly);
        return;
      } else if (
        planFromState === Plans.PRO_YEARLY ||
        planFromState === Plans.PRO
      ) {
        setPlan(Plans.PRO);
        setTab(SubscriptionTab.monthly);
        return;
      } else if (
        planFromState === Plans.AUTOPILOT_YEARLY ||
        planFromState === Plans.AUTOPILOT
      ) {
        setPlan(Plans.AUTOPILOT);
        setTab(SubscriptionTab.monthly);
        return;
      }
    }
    setPlan(planFromState as AddOnItemName | Plans);
  };

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED, {
      "Upgrade Dialog Source": upgradeDialogSource,
    });
    initializePlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFromState, upgradeDialogSource, keepTabForProDiscount]);

  useEffect(() => {
    return () => {
      // Unmount React component, should clear state properly.
      // For SubscribePage, only clear state - don't navigate since browser back handles it
      if (fromSubscribePage) {
        clearState();
        trackSubscription(true);
      } else {
        handleBack();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromSubscribePage]);

  useEffect(() => {
    if (
      currentOrg?.id &&
      (plan || splashPageSource === SplashPage.va_bundled)
    ) {
      trackSubscription();
      if (!customer?.id && Object.keys(addOns)?.length > 0) {
        getOrCreateCustomer(currentOrg?.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, plan, splashPageSource, Object.keys(addOns)?.length]);

  // Generate a SetupIntent (ask for their payment method without charging)
  // This will be stored on the Customer object by Stripe so that we can automatically
  // subscribe them.
  const needsSetupIntentSecret =
    !options?.clientSecret && currentOrg?.id && customer?.id;

  useEffect(() => {
    if (needsSetupIntentSecret) generateIntentSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSetupIntentSecret]);

  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) return <></>;

  const stripe = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

  const clearState = () => {
    setPlan(null);
    setSubscriptionPlan(null);
    setAddOnItems([]);
    setCustomer({
      name: currentOrg?.name || "",
      address: {
        country: "us",
        postal_code: "",
      },
    });
  };

  const handleBack = () => {
    trackEvent("Stripe Checkout - Back Button Clicked", {
      "Upgrade Dialog Source": upgradeDialogSource,
      "Splash Page Source": splashPageSource,
    });
    clearState();
    trackSubscription(true);

    // For SubscribePage, navigate back to the subscribe page
    if (fromSubscribePage) {
      navigate(-1);
      return;
    }

    if (onBack) {
      onBack();
      return;
    }
    if (
      upgradeDialogSource === UpgradeDialogSource.splash_page_annual_template
    ) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(SearchParams.UPGRADE_DIALOG_SOURCE);

      setSearchParams(newSearchParams);
      navigate(`/${routes.aboutOnboarding}?${newSearchParams.toString()}`, {
        replace: true,
      });
      return;
    }

    localStorage.removeItem(
      `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    );
    localStorage.removeItem(INSTAGRAM_ONBOARDING_CHOICE);
    if (splashPageSource && splashPageSource in SplashPageSourceBackRoute) {
      if (
        splashPageSource === SplashPage.va_bundled &&
        pageSource === `/${routes.virtualAssistant}`
      ) {
        navigate(`/${routes.virtualAssistant}`);
        return;
      }
      if (splashPageSource === SplashPage.va_money_back) {
        navigate(`/${pageSource}`);
        return;
      }
      if (splashPageSource === SplashPage.instagram) {
        if (
          localStorage.getItem(INSTAGRAM_ONBOARDING_FLOW) ===
          InstagramOnboardingFlowOption.standard
        ) {
          navigate(`/${routes.preferencesOnboarding}${location.search}`);
          return;
        }
        navigate(`/${routes.dashboard}`);
        return;
      }

      navigate(
        `${SplashPageSourceBackRoute[splashPageSource]}${location.search}`,
      );
      return;
    }
    if (isDialogOpen) {
      searchParams.set("checkout", "failed");
      setSearchParams(searchParams);
      setOpenCheckoutDialog(null);
    } else if (pageSource && pageSource !== undefined) {
      navigate(`${pageSource}?checkout=failed`);
    } else {
      navigate(`/${routes.settings}?checkout=failed`);
    }
  };

  const renderPaymentSide = () => {
    return (
      <>
        {loading || !invoice ? (
          <SkeletonPaymentForm />
        ) : (
          <>
            {customer?.payment_method && plan ? (
              <ConfirmPaymentForm
                onConfirm={handleConfirm}
                confirmLoading={confirmLoading}
                customer={customer}
                noNeedsToPay={noNeedsToPay}
                plan={plan}
              />
            ) : (
              <Elements stripe={stripe} options={options}>
                <Grid
                  item
                  xs={12}
                  lg={isDialogOpen ? 12 : 10}
                  xl={isDialogOpen ? 12 : 8}
                >
                  <PaymentForm
                    handleConfirm={handleConfirm}
                    returnUrl={getReturnUrl()}
                    setUpClientSecret={options?.clientSecret}
                    totalDue={totalDue}
                  />
                </Grid>
              </Elements>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <MainWrapper>
      <LeftSidebarWrapper>
        <Grid item xs={12} xl={isDialogOpen ? 12 : 9}>
          {!invoice ? (
            <SkeletonInvoiceForm />
          ) : (
            <>
              <PlanDetails
                details={details}
                handleBack={handleBack}
                noBackButton={noBackButton}
                trackSubscription={trackSubscription}
              />
              <InvoicePreview
                displayName={details?.display_name}
                productId={details?.product || ""}
                totalDue={totalDue}
              />
              {!isMobileScreen && (
                <Box sx={{ mt: 3, fontSize: 10, color: grey[500] }}>
                  Powered by{" "}
                  <Link href="https://onbento.com?s=checkout">Bento</Link>
                </Box>
              )}
            </>
          )}

          {isMobileScreen && renderPaymentSide()}
          {splashPageSource && isMobileScreen && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <NotInterestedButton trackSubscription={trackSubscription} />
            </Box>
          )}
        </Grid>
      </LeftSidebarWrapper>

      {!isMobileScreen && (
        <RightSidebarWrapper>{renderPaymentSide()}</RightSidebarWrapper>
      )}
    </MainWrapper>
  );
};

export default StripeCheckout;
